import {ECalendarValue} from '../common/types/calendar-value-enum';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  forwardRef,
  HostBinding,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChange,
  SimpleChanges,
  ViewEncapsulation
} from '@angular/core';
import {IMonth} from './month.model';
import {MonthCalendarService} from './month-calendar.service';
import * as momentNs from 'jalali-moment';
import {Moment} from 'jalali-moment';
import {IMonthCalendarConfig, IMonthCalendarConfigInternal} from './month-calendar-config';
import {
  ControlValueAccessor,
  FormControl,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ValidationErrors,
  Validator
} from '@angular/forms';
import {CalendarValue} from '../common/types/calendar-value';
import {UtilsService} from '../common/services/utils/utils.service';
import {DateValidator} from '../common/types/validator.type';
import {SingleCalendarValue} from '../common/types/single-calendar-value';
import {INavEvent} from '../common/models/navigation-event.model';
const /** @type {?} */ moment = momentNs;
export class MonthCalendarComponent implements OnInit, OnChanges, ControlValueAccessor, Validator {
   config: IMonthCalendarConfig;
   displayDate: Moment;
   minDate: Moment;
   maxDate: Moment;
    theme: string;

   onSelect: EventEmitter<IMonth> = new EventEmitter();
   onNavHeaderBtnClick: EventEmitter<null> = new EventEmitter();
   onGoToCurrent: EventEmitter<void> = new EventEmitter();
   onLeftNav: EventEmitter<INavEvent> = new EventEmitter();
   onRightNav: EventEmitter<INavEvent> = new EventEmitter();
   onLeftSecondaryNav: EventEmitter<INavEvent> = new EventEmitter();
   onRightSecondaryNav: EventEmitter<INavEvent> = new EventEmitter();

  isInited: boolean = false;
  componentConfig: IMonthCalendarConfigInternal;
  _selected: Moment[];
  yearMonths: IMonth[][];
  _currentDateView: Moment;
  inputValue: CalendarValue;
  inputValueType: ECalendarValue;
  validateFn: DateValidator;
  _shouldShowCurrent: boolean = true;
  navLabel: string;
  showLeftNav: boolean;
  showRightNav: boolean;
  showSecondaryLeftNav: boolean;
  showSecondaryRightNav: boolean;

  api = {
    toggleCalendar: this.toggleCalendarMode.bind(this),
    moveCalendarTo: this.moveCalendarTo.bind(this)
  };
/**
 * @param {?} selected
 * @return {?}
 */
set selected(selected: Moment[]) {
    this._selected = selected;
    this.onChangeCallback(this.processOnChangeCallback(selected));
  }
/**
 * @return {?}
 */
get selected(): Moment[] {
    return this._selected;
  }
/**
 * @param {?} current
 * @return {?}
 */
set currentDateView(current: Moment) {
    this._currentDateView = current.clone();
    this.yearMonths = this.monthCalendarService
      .generateYear(this.componentConfig, this._currentDateView, this.selected);
    this.navLabel = this.monthCalendarService.getHeaderLabel(this.componentConfig, this.currentDateView);
    this.showLeftNav = this.monthCalendarService.shouldShowLeft(this.componentConfig.min, this._currentDateView);
    this.showRightNav = this.monthCalendarService.shouldShowRight(this.componentConfig.max, this.currentDateView);
    this.showSecondaryLeftNav = this.componentConfig.showMultipleYearsNavigation && this.showLeftNav;
    this.showSecondaryRightNav = this.componentConfig.showMultipleYearsNavigation && this.showRightNav;
  }
/**
 * @return {?}
 */
get currentDateView(): Moment {
    return this._currentDateView;
  }
/**
 * @param {?} monthCalendarService
 * @param {?} utilsService
 * @param {?} cd
 */
constructor(public readonly monthCalendarService: MonthCalendarService,
public readonly utilsService: UtilsService,
public readonly cd: ChangeDetectorRef) {
  }
/**
 * @return {?}
 */
ngOnInit() {
    this.isInited = true;
    this.init();
    this.initValidators();
  }
/**
 * @param {?} changes
 * @return {?}
 */
ngOnChanges(changes: SimpleChanges) {
    if (this.isInited) {
      const {minDate, maxDate, config} = changes;

      this.handleConfigChange(config);
      this.init();

      if (minDate || maxDate) {
        this.initValidators();
      }
    }
  }
/**
 * @return {?}
 */
init() {
    this.componentConfig = this.monthCalendarService.getConfig(this.config);
    this.selected = this.selected || [];
    this.currentDateView = this.displayDate
      ? this.displayDate
      : this.utilsService
        .getDefaultDisplayDate(
          this.currentDateView,
          this.selected,
          this.componentConfig.allowMultiSelect,
          this.componentConfig.min,
          this.componentConfig.locale
        );
    this.inputValueType = this.utilsService.getInputType(this.inputValue, this.componentConfig.allowMultiSelect);
    this._shouldShowCurrent = this.shouldShowCurrent();
  }
/**
 * @param {?} value
 * @return {?}
 */
writeValue(value: CalendarValue): void {
    this.inputValue = value;

    if (value) {
      this.selected = this.utilsService
        .convertToMomentArray(value,
          this.componentConfig.format,
          this.componentConfig.allowMultiSelect,
          this.componentConfig.locale);
      this.yearMonths = this.monthCalendarService
        .generateYear(this.componentConfig, this.currentDateView, this.selected);
      this.inputValueType = this.utilsService.getInputType(this.inputValue, this.componentConfig.allowMultiSelect);
    }

    this.cd.markForCheck();
  }
/**
 * @param {?} fn
 * @return {?}
 */
registerOnChange(fn: any): void {
    this.onChangeCallback = fn;
  }
/**
 * @param {?} _
 * @return {?}
 */
onChangeCallback(_: any) {
  };
/**
 * @param {?} fn
 * @return {?}
 */
registerOnTouched(fn: any): void {
  }
/**
 * @param {?} formControl
 * @return {?}
 */
validate(formControl: FormControl): ValidationErrors | any {
    if (this.minDate || this.maxDate) {
      return this.validateFn(formControl.value);
    } else {
      return () => null;
    }
  }
/**
 * @return {?}
 */
isFarsi() {
    return this.componentConfig.locale === 'fa';
  }
/**
 * @param {?} value
 * @return {?}
 */
processOnChangeCallback(value: Moment[]): CalendarValue {
    return this.utilsService.convertFromMomentArray(
      this.componentConfig.format,
      value,
      this.componentConfig.returnedValueType || this.inputValueType,
      this.componentConfig.locale
    );
  }
/**
 * @return {?}
 */
initValidators() {
    this.validateFn = this.validateFn = this.utilsService.createValidator(
      {minDate: this.minDate, maxDate: this.maxDate},
      this.componentConfig.format,
      'month',
      this.componentConfig.locale
    );

    this.onChangeCallback(this.processOnChangeCallback(this.selected));
  }
/**
 * @param {?} month
 * @return {?}
 */
monthClicked(month: IMonth) {
    if (month.selected && !this.componentConfig.unSelectOnClick) {
      return;
    }

    this.selected = this.utilsService
      .updateSelected(this.componentConfig.allowMultiSelect, this.selected, month, 'month');
    this.yearMonths = this.monthCalendarService
      .generateYear(this.componentConfig, this.currentDateView, this.selected);
    this.onSelect.emit(month);
  }
/**
 * @return {?}
 */
onLeftNavClick() {
    const /** @type {?} */ from = this.currentDateView.clone();
    this.currentDateView = this.currentDateView.clone().subtract(1, 'year');
    const /** @type {?} */ to = this.currentDateView.clone();
    this.yearMonths = this.monthCalendarService.generateYear(this.componentConfig, this.currentDateView, this.selected);
    this.onLeftNav.emit({from, to});
  }
/**
 * @return {?}
 */
onLeftSecondaryNavClick() {
    let /** @type {?} */ navigateBy = this.componentConfig.multipleYearsNavigateBy;
    const /** @type {?} */ isOutsideRange = this.componentConfig.min &&
      this.currentDateView.year() - this.componentConfig.min.year() < navigateBy;

    if (isOutsideRange) {
      navigateBy = this.currentDateView.year() - this.componentConfig.min.year();
    }

    const /** @type {?} */ from = this.currentDateView.clone();
    this.currentDateView = this.currentDateView.clone().subtract(navigateBy, 'year');
    const /** @type {?} */ to = this.currentDateView.clone();
    this.onLeftSecondaryNav.emit({from, to});
  }
/**
 * @return {?}
 */
onRightNavClick() {
    const /** @type {?} */ from = this.currentDateView.clone();
    this.currentDateView = this.currentDateView.clone().add(1, 'year');
    const /** @type {?} */ to = this.currentDateView.clone();
    this.onRightNav.emit({from, to});
  }
/**
 * @return {?}
 */
onRightSecondaryNavClick() {
    let /** @type {?} */ navigateBy = this.componentConfig.multipleYearsNavigateBy;
    const /** @type {?} */ isOutsideRange = this.componentConfig.max &&
      this.componentConfig.max.year() - this.currentDateView.year() < navigateBy;

    if (isOutsideRange) {
      navigateBy = this.componentConfig.max.year() - this.currentDateView.year();
    }

    const /** @type {?} */ from = this.currentDateView.clone();
    this.currentDateView = this.currentDateView.clone().add(navigateBy, 'year');
    const /** @type {?} */ to = this.currentDateView.clone();
    this.onRightSecondaryNav.emit({from, to});
  }
/**
 * @return {?}
 */
toggleCalendarMode() {
    this.onNavHeaderBtnClick.emit();
  }
/**
 * @param {?} month
 * @return {?}
 */
getMonthBtnCssClass(month: IMonth): {[klass: string]: boolean} {
    const /** @type {?} */ cssClass: {[klass: string]: boolean} = {
      'dp-selected': month.selected,
      'dp-current-month': month.currentMonth
    };
    const /** @type {?} */ customCssClass: string = this.monthCalendarService.getMonthBtnCssClass(this.componentConfig, month.date);

    if (customCssClass) {
      cssClass[customCssClass] = true;
    }

    return cssClass;
  }
/**
 * @return {?}
 */
shouldShowCurrent(): boolean {
    return this.utilsService.shouldShowCurrent(
      this.componentConfig.showGoToCurrent,
      'month',
      this.componentConfig.min,
      this.componentConfig.max
    );
  }
/**
 * @return {?}
 */
goToCurrent() {
    this.currentDateView = moment().locale(this.componentConfig.locale);
    this.onGoToCurrent.emit();
  }
/**
 * @param {?} to
 * @return {?}
 */
moveCalendarTo(to: SingleCalendarValue) {
    if (to) {
      this.currentDateView = this.utilsService.convertToMoment(to, this.componentConfig.format, this.componentConfig.locale);
      this.cd.markForCheck();
    }
  }
/**
 * @param {?} config
 * @return {?}
 */
handleConfigChange(config: SimpleChange) {
    if (config) {
      const /** @type {?} */ prevConf: IMonthCalendarConfigInternal = this.monthCalendarService.getConfig(config.previousValue);
      const /** @type {?} */ currentConf: IMonthCalendarConfigInternal = this.monthCalendarService.getConfig(config.currentValue);

      if (this.utilsService.shouldResetCurrentView(prevConf, currentConf)) {
        this._currentDateView = null;
      }
    }
  }
static decorators: DecoratorInvocation[] = [
{ type: Component, args: [{
  selector: 'dp-month-calendar',
  template: `
    <div class="dp-month-calendar-container">
      <dp-calendar-nav
          [label]="navLabel"
          [showLeftNav]="showLeftNav"
          [showLeftSecondaryNav]="showSecondaryLeftNav"
          [showRightNav]="showRightNav"
          [showRightSecondaryNav]="showSecondaryRightNav"
          [isLabelClickable]="componentConfig.isNavHeaderBtnClickable"
          [showGoToCurrent]="shouldShowCurrent()"
          [theme]="theme"
          (onLeftNav)="onLeftNavClick()"
          (onLeftSecondaryNav)="onLeftSecondaryNavClick()"
          (onRightNav)="onRightNavClick()"
          (onRightSecondaryNav)="onRightSecondaryNavClick()"
          (onLabelClick)="toggleCalendarMode()"
          (onGoToCurrent)="goToCurrent()">
      </dp-calendar-nav>

      <div class="dp-calendar-wrapper" [ngClass]="{'rtl':isFarsi()}">
        <div class="dp-months-row" *ngFor="let monthRow of yearMonths">
          <button type="button"
                  class="dp-calendar-month"
                  *ngFor="let month of monthRow"
                  [attr.data-date]="month.date.format(componentConfig.format)"
                  [disabled]="month.disabled"
                  [ngClass]="getMonthBtnCssClass(month)"
                  (click)="monthClicked(month)"
                  [innerText]="month.text">
          </button>
        </div>
      </div>
    </div>
  `,
  styles: [`
    dp-month-calendar {
      display: inline-block;
    }
    dp-month-calendar .dp-month-calendar-container {
      background: #FFFFFF;
    }
    dp-month-calendar .dp-calendar-wrapper.rtl {
      direction: rtl;
    }
    dp-month-calendar .dp-calendar-month {
      -webkit-box-sizing: border-box;
              box-sizing: border-box;
      width: 55px;
      height: 55px;
      cursor: pointer;
    }
    dp-month-calendar .dp-calendar-month.dp-selected {
      background: rgba(16, 108, 200, 0.5);
      color: #FFFFFF;
    }
    dp-month-calendar.dp-material .dp-calendar-weekday {
      height: 25px;
      width: 30px;
      line-height: 25px;
      background: #E0E0E0;
      border: 1px solid #E0E0E0;
    }
    dp-month-calendar.dp-material .dp-calendar-wrapper {
      padding: 15px;
    }
    dp-month-calendar.dp-material .dp-calendar-month {
      -webkit-box-sizing: border-box;
              box-sizing: border-box;
      background: #FFFFFF;
      border-radius: 0;
      -webkit-transition: border-radius 0.1s ease;
      transition: border-radius 0.1s ease;
      border: none;
      outline: none;
      font-size: 0.7rem;
    }
    dp-month-calendar.dp-material .dp-calendar-month:hover {
      border-radius: 50%;
      background: #E0E0E0;
    }
    dp-month-calendar.dp-material .dp-selected {
      background: rgba(16, 108, 200, 0.5);
      color: #FFFFFF;
      border-radius: 50%;
    }
    dp-month-calendar.dp-material .dp-selected:hover {
      background: rgba(16, 108, 200, 0.5);
    }
    dp-month-calendar.dp-material .dp-current-month {
      border-radius: 50%;
      border: 1px solid rgba(16, 108, 200, 0.5);
      padding: 0;
    }
  `],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    MonthCalendarService,
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => MonthCalendarComponent),
      multi: true
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => MonthCalendarComponent),
      multi: true
    }
  ]
}, ] },
];
/**
 * @nocollapse
 */
static ctorParameters: () => ({type: any, decorators?: DecoratorInvocation[]}|null)[] = () => [
{type: MonthCalendarService, },
{type: UtilsService, },
{type: ChangeDetectorRef, },
];
static propDecorators: {[key: string]: DecoratorInvocation[]} = {
'config': [{ type: Input },],
'displayDate': [{ type: Input },],
'minDate': [{ type: Input },],
'maxDate': [{ type: Input },],
'theme': [{ type: HostBinding, args: ['class', ] },{ type: Input },],
'onSelect': [{ type: Output },],
'onNavHeaderBtnClick': [{ type: Output },],
'onGoToCurrent': [{ type: Output },],
'onLeftNav': [{ type: Output },],
'onRightNav': [{ type: Output },],
'onLeftSecondaryNav': [{ type: Output },],
'onRightSecondaryNav': [{ type: Output },],
};
}

function MonthCalendarComponent_tsickle_Closure_declarations() {
/** @type {?} */
MonthCalendarComponent.decorators;
/**
 * @nocollapse
 * @type {?}
 */
MonthCalendarComponent.ctorParameters;
/** @type {?} */
MonthCalendarComponent.propDecorators;
/** @type {?} */
MonthCalendarComponent.prototype.config;
/** @type {?} */
MonthCalendarComponent.prototype.displayDate;
/** @type {?} */
MonthCalendarComponent.prototype.minDate;
/** @type {?} */
MonthCalendarComponent.prototype.maxDate;
/** @type {?} */
MonthCalendarComponent.prototype.theme;
/** @type {?} */
MonthCalendarComponent.prototype.onSelect;
/** @type {?} */
MonthCalendarComponent.prototype.onNavHeaderBtnClick;
/** @type {?} */
MonthCalendarComponent.prototype.onGoToCurrent;
/** @type {?} */
MonthCalendarComponent.prototype.onLeftNav;
/** @type {?} */
MonthCalendarComponent.prototype.onRightNav;
/** @type {?} */
MonthCalendarComponent.prototype.onLeftSecondaryNav;
/** @type {?} */
MonthCalendarComponent.prototype.onRightSecondaryNav;
/** @type {?} */
MonthCalendarComponent.prototype.isInited;
/** @type {?} */
MonthCalendarComponent.prototype.componentConfig;
/** @type {?} */
MonthCalendarComponent.prototype._selected;
/** @type {?} */
MonthCalendarComponent.prototype.yearMonths;
/** @type {?} */
MonthCalendarComponent.prototype._currentDateView;
/** @type {?} */
MonthCalendarComponent.prototype.inputValue;
/** @type {?} */
MonthCalendarComponent.prototype.inputValueType;
/** @type {?} */
MonthCalendarComponent.prototype.validateFn;
/** @type {?} */
MonthCalendarComponent.prototype._shouldShowCurrent;
/** @type {?} */
MonthCalendarComponent.prototype.navLabel;
/** @type {?} */
MonthCalendarComponent.prototype.showLeftNav;
/** @type {?} */
MonthCalendarComponent.prototype.showRightNav;
/** @type {?} */
MonthCalendarComponent.prototype.showSecondaryLeftNav;
/** @type {?} */
MonthCalendarComponent.prototype.showSecondaryRightNav;
/** @type {?} */
MonthCalendarComponent.prototype.api;
/** @type {?} */
MonthCalendarComponent.prototype.monthCalendarService;
/** @type {?} */
MonthCalendarComponent.prototype.utilsService;
/** @type {?} */
MonthCalendarComponent.prototype.cd;
}


interface DecoratorInvocation {
  type: Function;
  args?: any[];
}
